import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Moon, Sun } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';

export default function Privacy() {
    const { t } = useTranslation();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();

    const handleBack = (e: React.MouseEvent) => {
        e.preventDefault();
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    return (
        <div className="min-h-screen bg-dark-bg text-text-main font-sans selection:bg-primary selection:text-white">
            <SEO
                title={t('legal.privacy.title')}
                description={t('legal.privacy.seo_description')}
                canonical="/legal/privacy"
            />

            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-bg/80 backdrop-blur-md border-b border-dark-border">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/" className="w-10 h-10 shrink-0">
                            <img src="/logo/app-logo.png" alt="Logo" className="w-full h-full object-contain" />
                        </Link>
                        <Link to="/" className="text-2xl font-bold text-text-main tracking-tight">CitoyenPro</Link>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-xl text-text-muted hover:bg-dark-hover hover:text-text-main transition-colors"
                        >
                            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                        <a
                            href="/"
                            onClick={handleBack}
                            className="flex items-center gap-2 text-sm font-medium text-text-muted hover:text-primary transition-colors"
                        >
                            <ArrowLeft size={16} />
                            {t('legal.nav.back')}
                        </a>
                    </div>
                </div>
            </nav>

            {/* Content */}
            <main className="pt-32 pb-20 px-6 max-w-4xl mx-auto">
                <h1 className="text-4xl md:text-5xl font-bold text-text-main mb-8">{t('legal.privacy.title')}</h1>
                <p className="text-text-muted mb-12">{t('legal.privacy.updated')}</p>

                <div className="space-y-12 text-lg text-text-muted leading-relaxed">
                    <section>
                        <h2 className="text-2xl font-bold text-text-main mb-4">{t('legal.privacy.section1.title')}</h2>
                        <p>
                            {t('legal.privacy.section1.content')}
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-text-main mb-4">{t('legal.privacy.section2.title')}</h2>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>{t('legal.privacy.section2.item1')}</li>
                            <li>{t('legal.privacy.section2.item2')}</li>
                            <li>{t('legal.privacy.section2.item3')}</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-text-main mb-4">{t('legal.privacy.section3.title')}</h2>
                        <p>
                            {t('legal.privacy.section3.content')}
                        </p>
                        <ul className="list-disc pl-6 space-y-2 mt-2">
                            <li>{t('legal.privacy.section3.item1')}</li>
                            <li>{t('legal.privacy.section3.item2')}</li>
                            <li>{t('legal.privacy.section3.item3')}</li>
                            <li>{t('legal.privacy.section3.item4')}</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-text-main mb-4">{t('legal.privacy.section4.title')}</h2>
                        <p>
                            {t('legal.privacy.section4.content')}
                        </p>
                        <ul className="list-disc pl-6 space-y-2 mt-2">
                            <li>{t('legal.privacy.section4.item1')}</li>
                            <li>{t('legal.privacy.section4.item2')}</li>
                            <li>{t('legal.privacy.section4.item3')}</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-text-main mb-4">{t('legal.privacy.section5.title')}</h2>
                        <p>
                            {t('legal.privacy.section5.content')}
                        </p>
                        <ul className="list-disc pl-6 space-y-2 mt-2">
                            <li>{t('legal.privacy.section5.item1')}</li>
                            <li>{t('legal.privacy.section5.item2')}</li>
                            <li>{t('legal.privacy.section5.item3')}</li>
                            <li>{t('legal.privacy.section5.item4')}</li>
                        </ul>
                        <p className="mt-4">
                            {t('legal.privacy.section5.contact')}
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-text-main mb-4">{t('legal.privacy.section6.title')}</h2>
                        <p>
                            {t('legal.privacy.section6.content')}
                        </p>
                    </section>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-dark-card border-t border-dark-border py-12 text-center">
                <p className="text-text-muted text-sm">{t('legal.footer.copyright')}</p>
            </footer>
        </div>
    );
}
